import type { DataOrigin, SourceVerification, VerificationDimension } from '@buakei/types';
import { CheckCircle2, CircleDashed, FlaskConical, HelpCircle, XCircle } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { cn } from '../cn';
import { Badge } from './badge';
import { Button, type ButtonProps } from './button';

/**
 * Persistent banner shown whenever demo content is on screen (UX-002, DESIGN-SYSTEM rule 4).
 * A genuine announcement, so it uses solid navy with white text (ADR-005).
 */
export function DemoDataBanner({ className }: { className?: string }) {
  return (
    <div
      role="region"
      aria-label="Demo experience notice"
      className={cn(
        'on-navy flex items-center gap-2 bg-navy-900 px-4 py-2 text-sm text-white',
        className,
      )}
    >
      <FlaskConical className="size-4 shrink-0" aria-hidden />
      <p>
        <strong className="font-semibold">Demo experience — not live legal research.</strong>{' '}
        Answers, drafts, demonstration sources, citations and people are fictitious. Acts in the
        Library are real documents, provisional and unverified.
      </p>
    </div>
  );
}

export function DataOriginBadge({ origin }: { origin: DataOrigin }) {
  if (origin === 'live') return null;
  return (
    <span
      className="text-xs whitespace-nowrap text-muted-600"
      title="Fictitious demonstration data"
    >
      Demo data
    </span>
  );
}

export const DIMENSION_LABELS: Record<VerificationDimension, { name: string; help: string }> = {
  authenticity: {
    name: 'Source authenticity',
    help: 'Identity and provenance of the document. Does not certify the extracted text.',
  },
  text_integrity: {
    name: 'Text accuracy',
    help: 'Extracted text checked against the original. Does not certify current law.',
  },
  legal_status: {
    name: 'Legal status',
    help: 'Bounded, as-of-date status assessment based on available amendment and commencement material.',
  },
  legal_analysis: {
    name: 'Legal analysis',
    help: 'Headnotes, holdings or propositions reviewed by a qualified legal editor.',
  },
};

type Tone = 'success' | 'danger' | 'plain';

/** A reviewed legal status, in words (ADR-023). */
const LEGAL_STATUS_WORDS: Record<
  NonNullable<SourceVerification['legal_status']['status']>,
  string
> = {
  in_force: 'In force',
  partly_in_force: 'Partly in force',
  not_commenced: 'Not commenced',
  repealed: 'Repealed',
  historical: 'Historical version',
};

function verdictPresentation(
  dimension: VerificationDimension,
  v: SourceVerification,
): {
  label: string;
  tone: Tone;
  icon: ReactNode;
} {
  const d = v[dimension];
  switch (d.verdict) {
    case 'reviewed': {
      if (dimension === 'legal_status') {
        // A status claim without an as-of date is not presentable as reviewed.
        const { asOf } = v.legal_status;
        if (!asOf) {
          return { label: 'Reviewed — as-of date missing', tone: 'plain', icon: <HelpCircle /> };
        }
        const status = v.legal_status.status;
        return {
          label: status ? `${LEGAL_STATUS_WORDS[status]} as of ${asOf}` : `Reviewed as of ${asOf}`,
          tone: 'success',
          icon: <CheckCircle2 />,
        };
      }
      return { label: 'Reviewed', tone: 'success', icon: <CheckCircle2 /> };
    }
    case 'pending':
      return { label: 'Editorial review pending', tone: 'plain', icon: <CircleDashed /> };
    case 'unknown':
      return { label: 'Could not be established', tone: 'plain', icon: <HelpCircle /> };
    case 'disputed':
      return { label: 'Disputed', tone: 'danger', icon: <XCircle /> };
    case 'rejected':
      return { label: 'Rejected', tone: 'danger', icon: <XCircle /> };
    case 'not_applicable':
      return { label: 'Not applicable', tone: 'plain', icon: <HelpCircle /> };
  }
}

/**
 * SourceStatusIndicator — shows each verification dimension separately, in words and icons,
 * never as a single "verified" flag (ADR-004 A; DESIGN-SYSTEM rule 2).
 */
export function VerificationDimensions({
  verification,
  compact = false,
}: {
  verification: SourceVerification;
  compact?: boolean;
}) {
  const dims = Object.keys(DIMENSION_LABELS) as VerificationDimension[];
  return (
    <dl className={cn('grid gap-2', compact ? 'text-xs' : 'text-sm')}>
      {dims.map((dim) => {
        const p = verdictPresentation(dim, verification);
        return (
          <div key={dim} className="flex flex-wrap items-center justify-between gap-2">
            <dt className="text-muted-600" title={DIMENSION_LABELS[dim].help}>
              {DIMENSION_LABELS[dim].name}
            </dt>
            <dd>
              <Badge tone={p.tone}>
                <span aria-hidden className="contents">
                  {p.icon}
                </span>
                {p.label}
              </Badge>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/**
 * A future capability rendered honestly: disabled, with the reason announced to assistive
 * technology (DESIGN-SYSTEM rule 4; ACCEPTANCE "buttons either work, are disabled with reasons…").
 */
export function PreviewAction({
  reason,
  children,
  ...props
}: Omit<ButtonProps, 'disabled' | 'variant' | 'size'> & { reason: string }) {
  const id = useId();
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <Button {...props} variant="link" disabled aria-describedby={id}>
        {children}
      </Button>
      <span id={id} className="text-xs text-muted-600">
        {reason}
      </span>
    </span>
  );
}
